import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { z } from 'zod';
import { BackendService } from './backend.service';
import { httpStatusOf } from '../core/http-error-status';
import {
  AZURE_VENDOR_ID,
  BEDROCK_VENDOR_ID,
  BYOK_VENDOR_IDS,
  GENERIC_BYOK_VENDOR_IDS,
  GITHUB_COPILOT_VENDOR_ID,
  VERTEX_VENDOR_ID,
  VendorId,
  isAzureVendor,
  isBedrockVendor,
  isGitHubCopilotVendor,
  isVertexVendor,
  normalizeBaseUrl,
  normalizeVendorId,
} from '../core/byok/byok-vendor';
import {
  DEFAULT_VERTEX_AUTH_MODE,
  VertexAuthMode,
  vendorHasField,
} from '../core/byok/provider-fields';

/**
 * Frontend client for the org-scoped Token Hub BYOK **registration** API
 * (`code-analyzer-service` `llm/tokenhub/registration/TokenHubRegistrationApiController`,
 * routes under `/api/token-hub-registration`).
 *
 * This is a *registration/provisioning* surface, NOT a key-resolution surface:
 * - Keys are scoped by `orgId + platform + vendor` (org-scoped, not per-user).
 * - Write operations (register/rotate/revoke) require the ADMIN role — non-admins get 403.
 * - The raw vendor secret is **never** returned; list responses carry only a masked hint.
 *
 * Token Hub never returns the raw key: it is resolved server-side at LLM-call time (the
 * backend proxy / gateway resolves it centrally). This is now the SOLE BYOK key path —
 * the former per-user/org KMS key store and the in-browser key have been removed. This
 * service exists so org admins can register/rotate/revoke BYOK keys with the platform
 * Token Hub and see what's registered. Auth/scoping rides the BFF session cookie + CSRF /
 * X-Org-Id headers injected by the global interceptors; org/user are resolved server-side.
 */
@Injectable({ providedIn: 'root' })
export class TokenHubByokService {
  private readonly http = inject(HttpClient);
  private readonly backend = inject(BackendService);

  /**
   * Vendors Token Hub accepts for a BYOK key. Re-exported from the vendor SSOT
   * (`core/byok/byok-vendor.ts`), which is the only module that spells the wire strings literally.
   */
  static readonly VENDORS = BYOK_VENDOR_IDS;

  /** The one vendor that REQUIRES baseUrl / apiVersion / deployments rather than allowing them. */
  static readonly AZURE_VENDOR = AZURE_VENDOR_ID;
  /** The org-managed, secret-free vendor whose descriptor is project/location/SA/WIF + bindings. */
  static readonly VERTEX_VENDOR = VERTEX_VENDOR_ID;
  /** The org-managed vendor whose descriptor is region + a WRITE-ONLY key + expiry + bindings. */
  static readonly BEDROCK_VENDOR = BEDROCK_VENDOR_ID;
  /** The org-managed, HUNT-ONLY vendor whose descriptor is a write-only PAT + a DISCOVERED catalog. */
  static readonly GITHUB_COPILOT_VENDOR = GITHUB_COPILOT_VENDOR_ID;
  /**
   * The vendors the GENERIC single-submit BYOK card may offer — {@link VENDORS} minus GitHub
   * Copilot, whose registration is two-phase and has its own card. See
   * `GENERIC_BYOK_VENDOR_IDS` for why the exclusion is structural rather than a branch.
   */
  static readonly GENERIC_VENDORS = GENERIC_BYOK_VENDOR_IDS;
  /** Platforms a BYOK key/license can be scoped to. */
  static readonly PLATFORMS = ['blue', 'purple', 'green'] as const;

  // Reactive shared cache of the org's registered BYOK keys (masked). Single source of truth
  // for BOTH the Token Hub card (the keys table) and the AI Config form (which gates provider
  // selection on key presence), so registering/revoking a key updates every consumer at once.
  private readonly _keys = signal<ByokKeySummary[]>([]);
  readonly keys = this._keys.asReadonly();
  private readonly _loading = signal(false);
  readonly loading = this._loading.asReadonly();
  private readonly _loaded = signal(false);
  readonly loaded = this._loaded.asReadonly();
  /** Vendors that currently have a registered key (an org key exists for them). */
  readonly registeredVendors = computed(() => new Set(this._keys().map((k) => k.vendor)));

  /**
   * Does the org have a registered BYOK key for `vendor`? Canonical key-presence signal for
   * gating provider/model selection across the app. Optimistic (returns `true`) until the key
   * list has loaded, so selection isn't wrongly blocked during the first/transient load — once
   * {@link loaded} is true it reflects real registration.
   */
  hasKeyFor(vendor: string): boolean {
    if (!this._loaded()) return true;
    return this.registeredVendors().has(byokVendorFor(vendor));
  }

  /**
   * The org's registered per-vendor credential configuration, or null when that vendor has no
   * registered key.
   *
   * **G1 — the one place that reads config off a list row.** The documented contract only promises
   * `baseUrl` / `apiVersion` / `deployments` for `azure_openai`; for every other vendor they are
   * parsed opportunistically and simply come back null when Token Hub omits them. That degrades to
   * "nothing to pre-fill", never to a rendered `undefined` and never to a failed key list. When the
   * backend starts echoing `baseUrl` for the other vendors, this function needs no change — the
   * values just start arriving.
   *
   * Reads the shared key cache, so it is only meaningful once {@link loaded} is true.
   */
  registeredConfigFor(vendor: string): RegisteredVendorConfig | null {
    const id = normalizeVendorId(vendor);
    const key = this._keys().find((k) => normalizeVendorId(k.vendor) === id);
    if (!key) return null;
    return {
      vendor: id,
      displayName: key.displayName?.trim() || null,
      baseUrl: normalizeBaseUrl(key.baseUrl) || null,
      apiVersion: key.apiVersion?.trim() || null,
      // Inactive deployments are returned by the API and kept here: hiding them would make a
      // rotate silently drop them (G3). Consumers that route calls filter on `active` themselves.
      deployments: key.deployments ?? [],
    };
  }

  /**
   * The org's registered Azure OpenAI routing config, or null when Azure is not registered or the
   * credential predates the Azure onboarding contract (so it carries no routing).
   *
   * This is the read-back that lets AI Configurations pre-fill its Azure fields instead of showing
   * an empty form after a successful registration. Azure-specific because its consumers need a
   * guaranteed `baseUrl` and only usable deployments; the general form is
   * {@link registeredConfigFor}.
   */
  readonly registeredAzure = computed<RegisteredAzureConfig | null>(() => {
    const config = this.registeredConfigFor(AZURE_VENDOR_ID);
    if (!config?.baseUrl) return null;
    return {
      baseUrl: config.baseUrl,
      apiVersion: config.apiVersion,
      deployments: config.deployments.filter((d) => d.active !== false),
    };
  });

  /**
   * The org's registered Google Vertex AI routing config, or null when Vertex is not registered.
   *
   * Reads the non-secret `vertex` block echoed on the key summary. Vertex is secret-free, so there
   * is nothing to mask — this block IS the summary. Only bindings Token Hub still considers usable
   * (`active !== false`) are exposed for the model picker; the full set (incl. inactive) stays in
   * the raw key row so a rotate does not silently drop one.
   */
  readonly registeredVertex = computed<RegisteredVertexConfig | null>(() => {
    const key = this._keys().find((k) => normalizeVendorId(k.vendor) === VERTEX_VENDOR_ID);
    const vertex = key?.vertex;
    if (!vertex) return null;
    return {
      authMode: vertex.authMode?.trim() || null,
      projectId: vertex.projectId?.trim() || null,
      location: vertex.location?.trim() || null,
      serviceAccountEmail: vertex.serviceAccountEmail?.trim() || null,
      workloadIdentityProvider: vertex.workloadIdentityProvider?.trim() || null,
      audience: vertex.audience?.trim() || null,
      models: (vertex.models ?? []).filter((m) => m.active !== false),
    };
  });

  /**
   * The org's registered Amazon Bedrock routing, or null when Bedrock is not registered.
   *
   * Reads the non-secret `bedrock` block echoed on the key summary. **There is no key hint here and
   * there never will be** — unlike an api-key vendor, Bedrock's masked status carries no key material
   * at all, not even a tail, so the region + bindings + expiry ARE the whole summary. A UI must offer
   * "replace key" rather than rendering a field it could pre-fill.
   *
   * `expiresAt` is surfaced deliberately: it is captured at registration precisely so a registration
   * cannot silently die on its expiry date.
   */
  readonly registeredBedrock = computed<RegisteredBedrockConfig | null>(() => {
    const key = this._keys().find((k) => normalizeVendorId(k.vendor) === BEDROCK_VENDOR_ID);
    const bedrock = key?.bedrock;
    if (!bedrock) return null;
    return {
      region: bedrock.region?.trim() || null,
      expiresAt: bedrock.expiresAt?.trim() || null,
      // Only bindings Token Hub still considers usable reach the model picker; the raw row keeps the
      // full set so a re-registration does not silently drop an inactive one.
      models: (bedrock.models ?? []).filter((m) => m.active !== false),
    };
  });

  /**
   * The org's registered GitHub Copilot registration, or null when Copilot is not registered.
   *
   * **The ONLY sanctioned source of "which Copilot models may I select?" (plan D-C5).** There is no
   * static Copilot catalog anywhere in this frontend and there must never be one: the model list is
   * a property of the CUSTOMER'S OWN registration, discovered by the server calling `listModels()`
   * with their credential. A Copilot Free seat sees exactly one model (`auto`, which GitHub resolves
   * per call); a paid seat sees whatever that account's plan exposes. A hardcoded list would name
   * models the org has no access to, and the dispatch-time catalog check would refuse every one.
   *
   * Three things a caller must not assume:
   *
   *  - **`status` is not always `active`.** Copilot's registration is two-phase: onboarding creates
   *    a `pending` generation, and only a successful `activate` — which requires the server to have
   *    listed models with that credential — promotes it. A `pending` registration serves NO
   *    inference and its models must not be offered.
   *  - **Every metadata field is nullable, and that is the contract.** Free's `auto` carries null
   *    capabilities and null billing; a shape that required them would reject the commonest seat.
   *  - **There is no credential here in any form.** The PAT is write-only. An ADMIN caller sees a
   *    masked tail that Token Hub computed (`maskedHint`); a MEMBER sees no hint and no auth mode at
   *    all — the backend narrows the projection by role, so this shape is legitimately sparser for a
   *    non-admin and that is not an error to repair.
   */
  readonly registeredGitHubCopilot = computed<RegisteredGitHubCopilotConfig | null>(() => {
    const key = this._keys().find(
      (k) => normalizeVendorId(k.vendor) === GITHUB_COPILOT_VENDOR_ID,
    );
    const copilot = key?.githubCopilot;
    if (!copilot) return null;
    return {
      status: copilot.status?.trim() || null,
      authMode: copilot.authMode?.trim() || null,
      maskedHint: copilot.maskedHint?.trim() || null,
      registrationId: copilot.registrationId?.trim() || null,
      registrationVersion: copilot.registrationVersion ?? null,
      catalogVersion: copilot.catalogVersion ?? null,
      catalogFetchedAt: copilot.catalogFetchedAt?.trim() || null,
      models: copilot.models ?? [],
    };
  });

  /**
   * True when the org has a Copilot registration that can actually serve inference.
   *
   * `registeredGitHubCopilot() !== null` is NOT the same question: since registration is synchronous
   * (D-C5 r3) a registration is always `active`, but one whose credential has no Copilot seat has an
   * EMPTY discovered catalog. Offering that empty list would present a credential nobody verified as
   * usable, so "can serve inference" means active AND with at least one discovered model.
   */
  readonly gitHubCopilotActive = computed<boolean>(() => {
    const config = this.registeredGitHubCopilot();
    return config?.status === 'active' && (config.models?.length ?? 0) > 0;
  });

  private get base(): string {
    return `${this.backend.backendUrl}/api/token-hub-registration`;
  }

  /** List the org's registered BYOK keys (masked). Any org member may read. */
  async listKeys(): Promise<ByokKeySummary[]> {
    const raw = await firstValueFrom(this.http.get<unknown>(`${this.base}/byok/keys`));
    return ListByokKeysSchema.parse(raw).keys;
  }

  /**
   * Refresh the shared key cache. Rethrows on failure so an interactive caller (the card) can
   * surface a toast; a passive caller (the form's provider-gating) should swallow it and stay
   * optimistic (see `loaded`).
   */
  async refresh(): Promise<void> {
    this._loading.set(true);
    try {
      this._keys.set(await this.listKeys());
      this._loaded.set(true);
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Register or rotate the org's credential for one vendor. ADMIN-only — a non-admin caller throws
   * {@link TokenHubForbiddenError}.
   *
   * **G2 — the single place that decides which endpoint a vendor's credential goes to.** Azure
   * OpenAI has its own onboarding route because its credential carries routing config (resource
   * URL, api-version, deployments) the generic route has no fields for; every other vendor uses the
   * generic one. Callers pass a vendor-agnostic {@link ByokCredentialInput} and never see the split,
   * so re-pointing a vendor when the backend contract changes is an edit to this method alone.
   *
   * Registration is **replace, not merge** (G3): the payload carries the vendor's full
   * configuration, and anything omitted is not preserved server-side. The caller is responsible for
   * pre-filling the form from {@link registeredConfigFor} so a rotate does not silently wipe config
   * the admin never saw.
   *
   * `platform` is intentionally NOT sent: it is a server-side deployment identity
   * (`integration.token-hub.platform`), not a per-key choice, so the backend applies it.
   */
  async registerCredential(input: ByokCredentialInput): Promise<ByokRegistrationResult> {
    const vendor = normalizeVendorId(input.vendor);
    try {
      if (isAzureVendor(vendor)) return await this.postAzureOnboarding(vendor, input);
      if (isVertexVendor(vendor)) return await this.postVertexOnboarding(vendor, input);
      if (isBedrockVendor(vendor)) return await this.postBedrockOnboarding(vendor, input);
      return await this.postGenericOnboarding(vendor, input);
    } catch (err) {
      throw mapTokenHubError(err);
    }
  }

  /**
   * Vertex's onboarding route. `vendor` is not sent — the backend fixes it to `google_vertex_ai`.
   * Vertex has TWO auth modes selected by a required `authMode`:
   *
   * - `service_account_key` (**primary/default**): a WRITE-ONLY service-account JSON key is sent
   *   alongside `location`, `models` and `displayName`. `projectId` / `serviceAccountEmail` are
   *   DERIVED server-side from the key, and the WIF fields do not exist, so none of them are sent.
   * - `workload_identity` (alternative, secret-free): the non-secret routing descriptor
   *   (`projectId` / `location` / `serviceAccountEmail` / `workloadIdentityProvider` / `audience`)
   *   plus `models`.
   *
   * The service-account key is never returned by any read path (write-only), so nothing here echoes
   * it back. `platform` is server-applied and deliberately not sent in either mode.
   */
  private async postVertexOnboarding(
    vendor: VendorId,
    input: ByokCredentialInput,
  ): Promise<ByokRegistrationResult> {
    const authMode = normalizeVertexAuthMode(input.authMode);
    const models = (input.models ?? []).map((m) => ({
      providerModelId: m.providerModelId,
      logicalModelId: m.logicalModelId,
      displayName: m.displayName ?? null,
    }));
    const body =
      authMode === 'service_account_key'
        ? {
            authMode,
            displayName: input.displayName ?? null,
            location: String(input.location ?? '').trim(),
            serviceAccountKeyJson: String(input.serviceAccountKeyJson ?? '').trim(),
            models,
          }
        : {
            authMode,
            displayName: input.displayName ?? null,
            projectId: String(input.projectId ?? '').trim(),
            location: String(input.location ?? '').trim(),
            serviceAccountEmail: String(input.serviceAccountEmail ?? '').trim(),
            workloadIdentityProvider: String(input.workloadIdentityProvider ?? '').trim(),
            audience: String(input.audience ?? '').trim(),
            models,
          };
    const raw = await firstValueFrom(
      this.http.post<unknown>(`${this.base}/byok/vertex/onboarding`, body),
    );
    const parsed = OnboardVertexByokTenantSchema.parse(raw);
    return {
      vendor,
      status: parsed.status,
      baseUrl: null,
      apiVersion: null,
      deployments: [],
      vertex: {
        authMode: parsed.authMode?.trim() || authMode,
        projectId: parsed.projectId,
        location: parsed.location,
        serviceAccountEmail: parsed.serviceAccountEmail,
        // Null in service-account-key mode — the WIF coordinates do not exist there.
        workloadIdentityProvider: parsed.workloadIdentityProvider,
        audience: parsed.audience,
        models: parsed.models.filter((m) => m.active !== false),
      },
    };
  }

  /**
   * Bedrock's onboarding route. `vendor` is not sent — the backend fixes it to `aws_bedrock`.
   *
   * Four properties of this request are load-bearing:
   *
   *  1. **No `authMode`.** Bedrock has exactly one auth mode. The backend asserts the field's absence
   *     structurally, so a copy-paste from {@link postVertexOnboarding} fails a test rather than a
   *     review — do not add one "for symmetry".
   *  2. **`region` is a separate required routing property**, never derived from the key (a long-term
   *     Bedrock API key is not Region-bound), and it decides the upstream host.
   *  3. **`providerModelId` travels VERBATIM.** No prefix added, none stripped, no case folding, no
   *     normalising of the inconsistent date suffixes. The backend matches hints WITHOUT stripping,
   *     so a `us.` value simply will not match an `eu.` binding.
   *  4. **The key is write-only.** Nothing here echoes it back and no read path ever returns it, so
   *     the response deliberately has no key field to parse.
   *
   * `platform` is server-applied and deliberately not sent, as for every other vendor.
   */
  private async postBedrockOnboarding(
    vendor: VendorId,
    input: ByokCredentialInput,
  ): Promise<ByokRegistrationResult> {
    const expiresAt = String(input.expiresAt ?? '').trim();
    const body = {
      displayName: input.displayName ?? null,
      region: String(input.region ?? '').trim(),
      // Passed straight through, deliberately untrimmed of internal content — the backend does not
      // trim the credential either, so trimming here could turn a working key into a broken one.
      apiKey: input.apiKey,
      // Omitted rather than sent null when absent: the field is optional and an explicit null adds
      // nothing the backend does not already default.
      ...(expiresAt ? { expiresAt } : {}),
      models: (input.models ?? []).map((m) => ({
        providerModelId: m.providerModelId,
        logicalModelId: m.logicalModelId,
        displayName: m.displayName ?? null,
      })),
    };
    const raw = await firstValueFrom(
      this.http.post<unknown>(`${this.base}/byok/bedrock/onboarding`, body),
    );
    const parsed = OnboardBedrockByokTenantSchema.parse(raw);
    return {
      vendor,
      status: parsed.status,
      baseUrl: null,
      apiVersion: null,
      deployments: [],
      bedrock: {
        region: parsed.region,
        expiresAt: parsed.expiresAt ?? null,
        models: parsed.models.filter((m) => m.active !== false),
      },
    };
  }

  /**
   * The server-owned Bedrock model catalog for `region` (or every region when omitted).
   *
   * This is the D-B11 endpoint, and the ONLY sanctioned source for "which models can I register?".
   * The six catalog rows are deliberately NOT hardcoded in the frontend: that would duplicate
   * server-owned data and drift the moment a row is added, in a place where drift produces a binding
   * the admission validator refuses rather than a visible error.
   *
   * It serves the PRE-REGISTRATION bindings editor, which must work before any registration exists.
   * The POST-registration model picker reads {@link registeredBedrock} instead, mirroring Vertex.
   *
   * Throws when the feature flag is off (404) — a caller must treat that as "Bedrock is unavailable
   * on this deployment", never as an empty catalog.
   */
  async fetchBedrockCatalog(region?: string | null): Promise<BedrockCatalog> {
    const trimmed = String(region ?? '').trim();
    const url = trimmed
      ? `${this.base}/byok/bedrock/catalog?region=${encodeURIComponent(trimmed)}`
      : `${this.base}/byok/bedrock/catalog`;
    const raw = await firstValueFrom(this.http.get<unknown>(url));
    return BedrockCatalogSchema.parse(raw);
  }

  /**
   * GitHub Copilot's SYNCHRONOUS registration (D-C5 r3): store the credential (the backend returns
   * `active`) and, in the same request, discover the catalog and write it back. `vendor` is not
   * sent — the backend fixes it to `github_copilot`.
   *
   * The registration is `active` whatever the verification outcome. `verified: false` means the
   * catalog discovery was refused — a structurally valid PAT belonging to an account with no Copilot
   * seat receives `403 unauthorized: not authorized to use this Copilot feature` from GitHub
   * (observed live) — so the registration is active with an empty catalog and a typed
   * `verificationErrorCode`; the admin fixes the seat and presses Test connection ({@link verifyGitHubCopilot}).
   * An entitlement refusal is **never** a bad-credential message (D-C10) — rotating the token would
   * not help.
   *
   * Four properties of this request are load-bearing:
   *
   *  1. **The PAT is write-only and travels once.** Nothing echoes it back, no read path returns it,
   *     and it is sent in the BODY — never a query string, which would reach access logs and browser
   *     history.
   *  2. **The token must be FINE-GRAINED** (`github_pat_`). Classic `ghp_` tokens are rejected by
   *     the backend with a typed 400 BEFORE any network call.
   *  3. **`authMode` is required**, unlike Bedrock's single-mode registration. Stage 1 implements
   *     `PAT` only; `GITHUB_APP` is frozen in the contract so the schema never changes, and the
   *     backend refuses it with `app_auth_not_enabled` until an operator raises
   *     `phx.github-copilot.app-auth-enabled`.
   *  4. **No catalog is sent.** A credential nobody has used cannot carry trustworthy model — or
   *     BILLING — metadata, and accepting one would let a caller assert it.
   *
   * `platform` is server-applied and deliberately not sent, as for every other vendor.
   */
  async postGitHubCopilotOnboarding(
    input: GitHubCopilotOnboardingInput,
  ): Promise<GitHubCopilotOnboarding> {
    try {
      const raw = await firstValueFrom(
        this.http.post<unknown>(`${this.base}/byok/github-copilot/onboarding`, {
          displayName: input.displayName?.trim() || null,
          authMode: input.authMode,
          // Passed straight through, deliberately untrimmed of internal content — the backend does
          // not trim the credential either, so trimming here could turn a working token into a
          // broken one. Omitted rather than sent null when this mode does not use it.
          ...(input.personalAccessToken ? { personalAccessToken: input.personalAccessToken } : {}),
          ...(input.appId ? { appId: input.appId } : {}),
          ...(input.installationId ? { installationId: input.installationId } : {}),
          ...(input.privateKeyPem ? { privateKeyPem: input.privateKeyPem } : {}),
          ...(input.anchorRepositoryId ? { anchorRepositoryId: input.anchorRepositoryId } : {}),
        }),
      );
      return OnboardGitHubCopilotSchema.parse(raw);
    } catch (err) {
      throw mapTokenHubError(err);
    }
  }

  /**
   * The org's cached Copilot catalog, refreshed by the server when older than
   * `phx.github-copilot.catalog-ttl`.
   *
   * Unlike {@link fetchBedrockCatalog} this is NOT a server-owned constant of what an admin MAY
   * register — it is what THIS org's credential can actually see, discovered with that credential.
   * Nothing here is selectable before a registration exists, which is why the pre-registration form
   * has no model picker at all: the models are a RESULT of registering, not an input to it.
   *
   * Throws when the feature flag is off (404) — treat that as "GitHub Copilot is unavailable on this
   * deployment", never as an empty catalog — and 502 when no activated registration exists.
   */
  async getGitHubCopilotCatalog(platform?: string | null): Promise<GitHubCopilotCatalog> {
    const trimmed = String(platform ?? '').trim();
    const url = trimmed
      ? `${this.base}/byok/github-copilot/catalog?platform=${encodeURIComponent(trimmed)}`
      : `${this.base}/byok/github-copilot/catalog`;
    try {
      const raw = await firstValueFrom(this.http.get<unknown>(url));
      return GitHubCopilotCatalogSchema.parse(raw);
    } catch (err) {
      throw mapTokenHubError(err);
    }
  }

  /**
   * Test the org's STORED Copilot credential — the Copilot equivalent of every other provider's
   * "Test connection", and like theirs it tests what is REGISTERED, never a token typed into the
   * form above it.
   *
   * It is a different call because Copilot is a different kind of provider: `/api/llm/connection-test`
   * probes `<baseUrl>/chat/completions`, and Copilot has no base URL at all — it is a CLI child, not
   * an inference API. The server runs the same model-list call activation uses.
   *
   * **A failed credential resolves, it does not reject.** `ok: false` with a server-written
   * `guidance` is the normal unhappy path; only a transport/flag failure throws (404 when the
   * feature flag is off). Callers must render the verdict, not treat `ok: false` as an error.
   */
  async verifyGitHubCopilot(platform?: string | null): Promise<GitHubCopilotVerifyResult> {
    const trimmed = String(platform ?? '').trim();
    const url = trimmed
      ? `${this.base}/byok/github-copilot/verify?platform=${encodeURIComponent(trimmed)}`
      : `${this.base}/byok/github-copilot/verify`;
    try {
      const raw = await firstValueFrom(this.http.post<unknown>(url, null));
      return VerifyGitHubCopilotSchema.parse(raw);
    } catch (err) {
      throw mapTokenHubError(err);
    }
  }

  /**
   * Azure's onboarding route. `vendor` is not sent — the backend fixes it to `azure_openai`, so it
   * cannot be sent misspelled.
   */
  private async postAzureOnboarding(
    vendor: VendorId,
    input: ByokCredentialInput,
  ): Promise<ByokRegistrationResult> {
    const raw = await firstValueFrom(
      this.http.post<unknown>(`${this.base}/byok/azure/onboarding`, {
        apiKey: input.apiKey,
        displayName: input.displayName ?? null,
        baseUrl: normalizeBaseUrl(input.baseUrl),
        apiVersion: input.apiVersion ?? '',
        deployments: (input.deployments ?? []).map((d) => ({
          deploymentName: d.deploymentName,
          logicalModelId: d.logicalModelId,
          displayName: d.displayName ?? null,
        })),
      }),
    );
    const parsed = OnboardAzureByokTenantSchema.parse(raw);
    return {
      vendor,
      status: parsed.status,
      baseUrl: normalizeBaseUrl(parsed.baseUrl) || null,
      apiVersion: parsed.apiVersion?.trim() || null,
      deployments: parsed.deployments,
    };
  }

  /**
   * The generic onboarding route (idempotent: ensures the Token Hub tenant + BYOK license exist,
   * then registers/rotates the key).
   *
   * `baseUrl` is sent only when the vendor's descriptor carries it, and only when non-blank. NOTE:
   * the analyzer's `OnboardByokTenantParams` does not declare `baseUrl` today and the service runs
   * with `fail-on-unknown-properties: false`, so the field is currently accepted and **silently
   * dropped** — the gateway override will not take effect until the backend DTO carries it. Sending
   * it is forward-compatible and cannot 400; see `docs/byok/OPEN-QUESTIONS.md` (G2).
   */
  private async postGenericOnboarding(
    vendor: VendorId,
    input: ByokCredentialInput,
  ): Promise<ByokRegistrationResult> {
    const baseUrl = vendorHasField(vendor, 'baseUrl') ? normalizeBaseUrl(input.baseUrl) : '';
    const raw = await firstValueFrom(
      this.http.post<unknown>(`${this.base}/byok/onboarding`, {
        vendor,
        apiKey: input.apiKey,
        displayName: input.displayName ?? null,
        ...(baseUrl ? { baseUrl } : {}),
      }),
    );
    const parsed = OnboardByokTenantSchema.parse(raw);
    return {
      vendor,
      status: parsed.keyStatus,
      baseUrl: baseUrl || null,
      apiVersion: null,
      deployments: [],
    };
  }

  /** Revoke the org's active key for `platform + vendor`. ADMIN-only. */
  async revokeKey(platform: ByokPlatform, vendor: ByokVendor): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<unknown>(
          `${this.base}/byok/keys/${encodeURIComponent(platform)}/${encodeURIComponent(vendor)}/revoke`,
          {},
        ),
      );
    } catch (err) {
      // 404 = nothing active to revoke for that platform/vendor — treat as already-gone.
      if (httpStatusOf(err) === 404) return;
      throw mapTokenHubError(err);
    }
  }
}

// ---- Typed errors so the UI can distinguish forbidden / unavailable from generic failures ----

/** Caller lacks the ADMIN role required for a Token Hub write. */
export class TokenHubForbiddenError extends Error {
  constructor() {
    super('ADMIN role is required to manage Token Hub BYOK keys.');
    this.name = 'TokenHubForbiddenError';
  }
}

/** Token Hub itself is unreachable (503) or returned an invalid response (502). */
export class TokenHubUnavailableError extends Error {
  constructor() {
    super('Token Hub is currently unavailable. Please try again later.');
    this.name = 'TokenHubUnavailableError';
  }
}

function mapTokenHubError(err: unknown): unknown {
  const status = httpStatusOf(err);
  if (status === 403) return new TokenHubForbiddenError();
  if (status === 502 || status === 503) return new TokenHubUnavailableError();
  return err;
}

/**
 * Coerce an arbitrary (possibly absent/legacy) `authMode` string to a known Vertex mode. Anything
 * that is not an exact known mode falls back to {@link DEFAULT_VERTEX_AUTH_MODE} (the primary,
 * key-based mode), matching the card's default so the request body is well-formed.
 */
function normalizeVertexAuthMode(raw: string | null | undefined): VertexAuthMode {
  return raw === 'service_account_key' || raw === 'workload_identity'
    ? raw
    : DEFAULT_VERTEX_AUTH_MODE;
}

// ---- Request / response contracts (mirror TokenHubRegistrationDtos) ----

/** Alias of the SSOT {@link VendorId}, kept so existing imports of `ByokVendor` still resolve. */
export type ByokVendor = VendorId;
export type ByokPlatform = (typeof TokenHubByokService.PLATFORMS)[number];

/**
 * Token Hub BYOK vendor id for an `LlmProvider`. Every provider id IS its vendor id except Azure
 * OpenAI, whose key is registered (and resolved server-side) under vendor `azure_openai` —
 * mirroring `LlmUpstreamForwarder.tokenHubVendorFor` on the backend. Accepts a plain string so
 * provider ids, the older `azure` vendor id, and already-normalised vendor ids all map correctly.
 */
export const byokVendorFor = normalizeVendorId;

/**
 * A vendor's full credential configuration, as submitted. Azure-only fields are optional in the
 * type and required per-vendor by `PROVIDER_FIELDS`, so one shape serves every vendor and the
 * caller strips what the active vendor does not carry.
 */
export interface ByokCredentialInput {
  vendor: ByokVendor;
  /** Every vendor except Vertex requires a key; Vertex is secret-free (may be blank/omitted). */
  apiKey: string;
  displayName?: string | null;
  /** OpenAI-compatible gateway URL (optional) / Azure resource URL (required). */
  baseUrl?: string | null;
  /** Azure only: data-plane API version. */
  apiVersion?: string | null;
  /** Azure only: the resource's deployments. At least one. */
  deployments?: readonly AzureDeploymentInput[];
  // ── Vertex only ───────────────────────────────────────────────────────────
  /** Vertex only: which auth mode the registration uses (`service_account_key` | `workload_identity`). */
  authMode?: string | null;
  /** Vertex service-account-key mode only: the WRITE-ONLY service-account JSON key. */
  serviceAccountKeyJson?: string | null;
  /** Vertex workload-identity mode only: GCP project id hosting Vertex AI. */
  projectId?: string | null;
  /** Vertex only: Vertex AI region, or `global`. Both modes. */
  location?: string | null;
  /** Vertex workload-identity mode only: the customer service account Phoenix impersonates at runtime. */
  serviceAccountEmail?: string | null;
  /** Vertex workload-identity mode only: the WIF provider resource name. */
  workloadIdentityProvider?: string | null;
  /** Vertex workload-identity mode only: the WIF audience presented at the STS token exchange. */
  audience?: string | null;
  /** Vertex only: publisher-model → logical-model bindings. At least one. Both modes. */
  models?: readonly VertexModelBindingInput[] | readonly BedrockModelBindingInput[];
  // ── Bedrock only ──────────────────────────────────────────────────────────
  /**
   * Bedrock only: the AWS Region the registration is pinned to. A REQUIRED routing property, never
   * derived from the key, and the thing that decides both the upstream host and which model ids are
   * valid. Deliberately NOT named `location` — that is Vertex's field and the two are disjoint.
   */
  region?: string | null;
  /**
   * Bedrock only: the long-term key's expiry, ISO-8601 with a zone. Optional. Captured so a
   * registration cannot silently die on its expiry date.
   */
  expiresAt?: string | null;
}

/** Vendor-agnostic result of a registration, normalised across the onboarding routes. */
export interface ByokRegistrationResult {
  vendor: ByokVendor;
  /** Token Hub's status string, e.g. `registered` / `active`. */
  status: string;
  baseUrl: string | null;
  apiVersion: string | null;
  deployments: AzureDeploymentSummary[];
  /** Vertex only: the registered non-secret routing + bindings echoed back. Null otherwise. */
  vertex?: RegisteredVertexConfig | null;
  /**
   * Bedrock only: the registered non-secret routing + bindings echoed back. Null otherwise.
   * NEVER carries the API key — it is write-only and no response shape has a field for it.
   */
  bedrock?: RegisteredBedrockConfig | null;
}

/** What the org has registered for one vendor, as read back from the key list. */
export interface RegisteredVendorConfig {
  vendor: ByokVendor;
  displayName: string | null;
  baseUrl: string | null;
  apiVersion: string | null;
  /** Includes `active: false` rows — see {@link TokenHubByokService.registeredConfigFor}. */
  deployments: AzureDeploymentSummary[];
}

/** The org's Azure routing as registered in Token Hub, for pre-filling AI Configurations. */
export interface RegisteredAzureConfig {
  baseUrl: string;
  apiVersion: string | null;
  deployments: AzureDeploymentSummary[];
}

/** One Vertex publisher-model → logical-model binding, as submitted. All non-secret. */
export interface VertexModelBindingInput {
  /** Vertex publisher model id, sent on the wire as `google/{providerModelId}`. */
  providerModelId: string;
  /** Canonical Phoenix model id usage and pricing are recorded against. */
  logicalModelId: string;
  displayName?: string | null;
}

/**
 * The org's Vertex routing + bindings as registered in Token Hub. The service-account key (when the
 * registration used key mode) is WRITE-ONLY and never carried here.
 */
export interface RegisteredVertexConfig {
  /** Which auth mode the registration uses (`service_account_key` | `workload_identity`), or null. */
  authMode: string | null;
  projectId: string | null;
  location: string | null;
  serviceAccountEmail: string | null;
  /** Null when the registration used service-account-key mode (no WIF coordinates there). */
  workloadIdentityProvider: string | null;
  audience: string | null;
  /** Only bindings Token Hub still considers usable (`active !== false`). */
  models: VertexModelBindingSummary[];
}

/**
 * One Amazon Bedrock model binding, as submitted. All non-secret.
 *
 * A DISJOINT SIBLING of {@link VertexModelBindingInput} despite the identical field names (D-B8) —
 * the semantics differ in a way that matters: a Vertex `providerModelId` is a publisher model id
 * that the backend WRAPS (`google/{id}`), whereas a Bedrock `providerModelId` is an opaque
 * inference-profile id sent VERBATIM in the request body, whose `eu.`/`us.` geography prefix is part
 * of the id and whose date suffix is inconsistent between models by design.
 */
export interface BedrockModelBindingInput {
  /** Bedrock model / inference-profile id. Sent verbatim; never derived, prefixed or normalised. */
  providerModelId: string;
  /** Canonical Phoenix model id usage and pricing are recorded against. */
  logicalModelId: string;
  displayName?: string | null;
}

/**
 * The org's Bedrock routing + bindings as registered in Token Hub.
 *
 * **There is no key field, and no masked hint either.** Bedrock's API key is write-only and appears
 * on no read projection at all — not even a tail — so the region, bindings and expiry are the whole
 * summary. A UI offers "replace key"; it never renders a field it could pre-fill.
 */
export interface RegisteredBedrockConfig {
  /** The AWS Region the registration is pinned to. Decides the upstream host and the valid ids. */
  region: string | null;
  /** The registered key's expiry, when one was supplied. Surface it with rotation guidance. */
  expiresAt: string | null;
  /** Only bindings Token Hub still considers usable (`active !== false`). */
  models: BedrockModelBindingSummary[];
}

/**
 * GitHub Copilot's two credential modes, mirroring the frozen contract's `authMode` discriminator.
 * **Stage 1 implements `PAT` only** — `GITHUB_APP` is frozen now so the schema never changes, and
 * the backend refuses it with `app_auth_not_enabled` until an operator raises
 * `phx.github-copilot.app-auth-enabled`.
 */
export const GITHUB_COPILOT_AUTH_MODES = ['PAT', 'GITHUB_APP'] as const;
export type GitHubCopilotAuthMode = (typeof GITHUB_COPILOT_AUTH_MODES)[number];

/** The primary (and, in Stage 1, only implemented) Copilot credential mode. */
export const DEFAULT_GITHUB_COPILOT_AUTH_MODE: GitHubCopilotAuthMode = 'PAT';

/**
 * What is submitted to phase 1 of Copilot onboarding. Every credential field is WRITE-ONLY: it is
 * sent once, in the body, and appears on no read path in any form.
 */
export interface GitHubCopilotOnboardingInput {
  authMode: GitHubCopilotAuthMode;
  displayName?: string | null;
  /** PAT mode: a FINE-GRAINED token (`github_pat_`). Classic `ghp_` is a typed 400, pre-network. */
  personalAccessToken?: string | null;
  // ── GITHUB_APP mode (Stage 2; declared so the schema never changes) ────────────────────────
  appId?: string | null;
  installationId?: string | null;
  /** Write-only PEM. Same handling as the PAT: sent once, never echoed. */
  privateKeyPem?: string | null;
  anchorRepositoryId?: string | null;
}

/**
 * The org's Copilot registration as read back from the key list.
 *
 * Sparser for a non-admin caller BY DESIGN — the backend narrows this projection by role, so a
 * member sees `status`, the label and the catalog, and null for `authMode` / `maskedHint` /
 * `registrationId` / every catalog-version field. That is not a degraded response to repair.
 */
export interface RegisteredGitHubCopilotConfig {
  /** `active` — registration is synchronous (D-C5 r3). An active registration with an empty catalog is registered-but-unverified. */
  status: string | null;
  /** ADMIN only. */
  authMode: string | null;
  /** ADMIN only: a masked TAIL Token Hub computed. NEVER key material, and never re-derived here. */
  maskedHint: string | null;
  /** ADMIN only. */
  registrationId: string | null;
  /** ADMIN only. */
  registrationVersion: number | null;
  /** ADMIN only. */
  catalogVersion: number | null;
  /** ADMIN only: when the server last verified the catalog against GitHub. */
  catalogFetchedAt: string | null;
  /** The DISCOVERED model catalog. Never a hardcoded list (D-C5). */
  models: GitHubCopilotModelSummary[];
}

/** One row of the server-owned Bedrock catalog — what an admin may SELECT, never author. */
export interface BedrockCatalogModel {
  region: string;
  /** Opaque; select it, send it verbatim, never construct or edit it. */
  providerModelId: string;
  logicalModelId: string;
  displayName: string;
  capabilities: {
    streaming: boolean;
    tools: boolean;
    vision: boolean;
    maxOutputTokens: number;
  };
}

/** One deployment inside the customer's Azure OpenAI resource. */
export interface AzureDeploymentInput {
  /** Deployment name as it appears in Azure. This is what routes the request. */
  deploymentName: string;
  /** Canonical model behind the deployment. Usage is priced against this, not the name. */
  logicalModelId: string;
  displayName?: string | null;
}

const AzureDeploymentSchema = z.object({
  deploymentId: z.string().nullable(),
  deploymentName: z.string(),
  logicalModelId: z.string(),
  displayName: z.string().nullable(),
  active: z.boolean(),
});
export type AzureDeploymentSummary = z.infer<typeof AzureDeploymentSchema>;

const VertexModelBindingSchema = z.object({
  providerModelId: z.string(),
  logicalModelId: z.string(),
  displayName: z.string().nullable(),
  active: z.boolean(),
});
export type VertexModelBindingSummary = z.infer<typeof VertexModelBindingSchema>;

const BedrockModelBindingSchema = z.object({
  providerModelId: z.string(),
  logicalModelId: z.string(),
  displayName: z.string().nullable(),
  active: z.boolean(),
});
export type BedrockModelBindingSummary = z.infer<typeof BedrockModelBindingSchema>;

// Bedrock's non-secret routing echo on a key summary. `.nullish()` throughout, like the Vertex
// sibling, so a Token Hub that omits a field degrades to "no pre-fill" rather than failing the whole
// key list. Note what is NOT here and never will be: any key field, masked or otherwise.
const BedrockRoutingSummarySchema = z.object({
  region: z.string().nullish(),
  expiresAt: z.string().nullish(),
  models: BedrockModelBindingSchema.array().nullish(),
});

// Vertex's non-secret routing echo on a key summary. `.nullish()` throughout so a Token Hub that
// omits a field degrades to "no pre-fill" rather than failing the whole key list.
const VertexRoutingSummarySchema = z.object({
  authMode: z.string().nullish(),
  projectId: z.string().nullish(),
  location: z.string().nullish(),
  serviceAccountEmail: z.string().nullish(),
  workloadIdentityProvider: z.string().nullish(),
  audience: z.string().nullish(),
  models: VertexModelBindingSchema.array().nullish(),
});

// ── GitHub Copilot (C-T5) ──────────────────────────────────────────────────────────────────────
//
// EVERY metadata field is `.nullish()`, and that is the CONTRACT rather than defensive habit: a
// Copilot Free seat sees exactly one model, `auto`, whose capabilities, billing and policy blocks
// are all null — observed live in the Stage 0 spike. A schema that required them would reject the
// most common trial seat outright. `providerModelId` and `modelBindingRef` are each nullish for a
// second reason: the ADMIN projection carries the first, the MEMBER projection the second.

const GitHubCopilotModelCapabilitiesSchema = z.object({
  reasoningEfforts: z.string().array().nullish(),
  maxContextWindowTokens: z.number().nullish(),
  supportsVision: z.boolean().nullish(),
});

const GitHubCopilotModelSchema = z.object({
  providerModelId: z.string().nullish(),
  modelBindingRef: z.string().nullish(),
  logicalModelId: z.string(),
  displayName: z.string().nullish(),
  capabilities: GitHubCopilotModelCapabilitiesSchema.nullish(),
  /** ADMIN only. A member picks a model; they do not price one. */
  billingMultiplier: z.number().nullish(),
});
export type GitHubCopilotModelSummary = z.infer<typeof GitHubCopilotModelSchema>;

// Copilot's non-secret registration echo on a key summary. There is deliberately NO
// `personalAccessToken` field here, not even a nullable one — the absence IS the contract, so there
// is nothing for a future edit to start relaying.
const GitHubCopilotRoutingSummarySchema = z.object({
  status: z.string().nullish(),
  authMode: z.string().nullish(),
  maskedHint: z.string().nullish(),
  registrationId: z.string().nullish(),
  registrationVersion: z.number().nullish(),
  catalogVersion: z.number().nullish(),
  catalogFetchedAt: z.string().nullish(),
  models: GitHubCopilotModelSchema.array().nullish(),
});

const ByokKeySummarySchema = z.object({
  orgId: z.string(),
  platform: z.string().nullable(),
  vendor: z.string(),
  displayName: z.string().nullable(),
  apiKey: z.string().nullable(),
  // Azure OpenAI only, and only when Token Hub echoes the routing config on the list endpoint.
  // Non-secret. `.nullish()` (not `.nullable()`) because a Token Hub that omits them entirely must
  // degrade to "no pre-fill", never fail the whole key list and blank the BYOK card.
  baseUrl: z.string().nullish(),
  apiVersion: z.string().nullish(),
  deployments: AzureDeploymentSchema.array().nullish(),
  // Google Vertex AI only: the non-secret routing descriptor + bindings. Null for other vendors.
  vertex: VertexRoutingSummarySchema.nullish(),
  // Amazon Bedrock only: region + bindings + key expiry. Null for other vendors. Carries NO key
  // material of any kind — Bedrock's key is write-only and has no masked hint, so the generic
  // `apiKey` field above is null for a Bedrock row too.
  bedrock: BedrockRoutingSummarySchema.nullish(),
  // GitHub Copilot only: status + the discovered catalog, plus (ADMIN only) the auth mode, the
  // registration identity and a masked credential TAIL. Null for other vendors. The generic
  // `apiKey` field above is null for a Copilot row too — the PAT is write-only and has no hint on
  // either projection.
  githubCopilot: GitHubCopilotRoutingSummarySchema.nullish(),
});
export type ByokKeySummary = z.infer<typeof ByokKeySummarySchema>;

const ListByokKeysSchema = z.object({
  orgId: z.string(),
  keys: ByokKeySummarySchema.array(),
});

const OnboardByokTenantSchema = z.object({
  orgId: z.string(),
  platform: z.string(),
  vendor: z.string(),
  displayName: z.string().nullable(),
  byokActive: z.boolean(),
  keyStatus: z.string(),
});
export type OnboardByokTenant = z.infer<typeof OnboardByokTenantSchema>;

const OnboardAzureByokTenantSchema = z.object({
  orgId: z.string(),
  platform: z.string(),
  vendor: z.string(),
  displayName: z.string().nullable(),
  baseUrl: z.string(),
  apiVersion: z.string(),
  status: z.string(),
  deployments: AzureDeploymentSchema.array(),
});
export type OnboardAzureByokTenant = z.infer<typeof OnboardAzureByokTenantSchema>;

const OnboardVertexByokTenantSchema = z.object({
  orgId: z.string(),
  platform: z.string(),
  vendor: z.string(),
  status: z.string(),
  displayName: z.string().nullable(),
  // The backend always echoes authMode; `.nullish()` (defaulted to WIF at the read site) keeps an
  // older Token Hub that omits it from failing the whole parse.
  authMode: z.string().nullish(),
  registrationId: z.string().nullish(),
  registrationVersion: z.number().nullish(),
  // Derived server-side (from the SA key in key mode; supplied in WIF mode) — always present.
  projectId: z.string(),
  location: z.string(),
  serviceAccountEmail: z.string(),
  // Null in service-account-key mode: the WIF coordinates do not exist there.
  workloadIdentityProvider: z.string().nullable(),
  audience: z.string().nullable(),
  models: VertexModelBindingSchema.array(),
});
export type OnboardVertexByokTenant = z.infer<typeof OnboardVertexByokTenantSchema>;

// The Bedrock onboarding response. `region` and `models` are always present (the backend has just
// registered them); `expiresAt` is nullish because the customer may not have supplied one, and
// registrationId/registrationVersion are nullish so an older Token Hub omitting them does not fail
// the parse. There is deliberately NO key field and NO authMode field — asserting their absence is
// part of what the spec suite checks.
const OnboardBedrockByokTenantSchema = z.object({
  orgId: z.string(),
  platform: z.string(),
  vendor: z.string(),
  status: z.string(),
  displayName: z.string().nullable(),
  registrationId: z.string().nullish(),
  registrationVersion: z.number().nullish(),
  region: z.string(),
  expiresAt: z.string().nullish(),
  models: BedrockModelBindingSchema.array(),
});
export type OnboardBedrockByokTenant = z.infer<typeof OnboardBedrockByokTenantSchema>;

const BedrockCatalogModelSchema = z.object({
  region: z.string(),
  providerModelId: z.string(),
  logicalModelId: z.string(),
  displayName: z.string(),
  capabilities: z.object({
    streaming: z.boolean(),
    tools: z.boolean(),
    vision: z.boolean(),
    maxOutputTokens: z.number(),
  }),
});

/**
 * The D-B11 catalog response. Strict (not `.nullish()`) on every field, unlike the key-summary
 * schemas: those degrade to "no pre-fill" when Token Hub omits something, but this endpoint is
 * Purple's OWN backend serving its own compiled constant. A missing field there is a deployment
 * mismatch, and failing the parse is the honest outcome — silently offering a partial catalog would
 * let an admin register against data the server does not actually have.
 */
const BedrockCatalogSchema = z.object({
  regions: z.string().array(),
  region: z.string().nullish(),
  models: BedrockCatalogModelSchema.array(),
});
export type BedrockCatalog = z.infer<typeof BedrockCatalogSchema>;

// The verdict of an explicit connection test against the STORED credential. Deliberately a
// verdict object rather than a thrown error on failure: a dead credential is the ANSWER here, and
// the server's `guidance` is the only thing that tells an admin what to do about it — a thrown
// error would render as a generic failure and lose exactly that.
const VerifyGitHubCopilotSchema = z.object({
  orgId: z.string(),
  ok: z.boolean(),
  registrationId: z.string().nullish(),
  modelCount: z.number().nullish(),
  errorCode: z.string().nullish(),
  guidance: z.string().nullish(),
  checkedAt: z.string(),
});
export type GitHubCopilotVerifyResult = z.infer<typeof VerifyGitHubCopilotSchema>;

// ── GitHub Copilot, continued ───────────────────────────────────────────────────────────────
//
// SYNCHRONOUS onboarding (D-C5 r3): `status` is ALWAYS `active`, and the response carries the
// verification result of the catalog discovery that ran in the same request. `verified: false`
// means the registration is active with an EMPTY catalog and a typed `verificationErrorCode` (no
// Copilot seat, runtime unavailable, …) — the card renders it and offers Test connection. `status`
// is parsed rather than assumed, so a contract change is visible here instead of silently trusted.
const OnboardGitHubCopilotSchema = z.object({
  orgId: z.string(),
  platform: z.string(),
  vendor: z.string(),
  status: z.string(),
  displayName: z.string().nullish(),
  registrationId: z.string(),
  registrationVersion: z.number(),
  authMode: z.string(),
  verified: z.boolean(),
  models: GitHubCopilotModelSchema.array(),
  catalogVersion: z.number().nullish(),
  tokenOwnerLogin: z.string().nullish(),
  verificationErrorCode: z.string().nullish(),
  verificationGuidance: z.string().nullish(),
});
export type GitHubCopilotOnboarding = z.infer<typeof OnboardGitHubCopilotSchema>;

const GitHubCopilotCatalogSchema = z.object({
  orgId: z.string(),
  registrationId: z.string(),
  registrationVersion: z.number(),
  catalogVersion: z.number().nullish(),
  fetchedAt: z.string().nullish(),
  refreshed: z.boolean(),
  models: GitHubCopilotModelSchema.array(),
});
export type GitHubCopilotCatalog = z.infer<typeof GitHubCopilotCatalogSchema>;
